/* eslint-disable react/prop-types */

import { useEffect, useRef, useState } from "react";

import styles from "./defaultSuggestionsMobile.module.scss";
import DEFAULT_SUGGESTIONS from "./constants";
import CitySuggestionsMobile from "../CitySuggestionsMobile";
import { getSearchSuggestions } from "@Components/SearchModalMobile/data/helper";
// import CitySuggestionsDefault from "../CitySuggestionsDefault";
import Image from "@Components/Image";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useIsDesktop from "hooks/useIsDesktop";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import useWhitelabel from "hooks/useWhitelabel";
import { WHITELABEL_DEFAULT_SUGGESTIONS } from "@Components/SearchModalDesktop/components/DefaultSuggestionsDesktop/constants";
import NearMeOption from "@Components/NearMeOption/NearMeOption";
import { useHistory } from "hooks/useHistory";
import customHistoryPush from "@Utils/customHistoryPush";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import config from "@Config/index";

const FLAG_BASE_URL = "https://prod-static-assets.amberstudent.com/images/";

const getLocalLanguageRegionName = (title: string, t: any) => {
  const [titlePart, regionName] = title.split(" in ");
  if (titlePart && regionName) {
    const translatedTitle = t(`${titlePart} in`);
    return `${translatedTitle} ${regionName}`;
  }
  return t(titlePart);
};

const getDynamicCitiesLabel = (originalLabel: string, dataCount: number, t: any) => {
  const match = originalLabel.match(/Top Cities? in (.+)/);
  if (match && match[1]) {
    const countryName = match[1];
    const cityWord = dataCount === 1 ? t("home.searchbar.topcity") : t("home.searchbar.topcities");
    return `${cityWord} ${t("home.searchbar.topcitiesin")} ${countryName}`;
  }
  if (originalLabel === "Top Cities" || originalLabel === "Top City") {
    return dataCount === 1 ? "Top City" : "Top Cities";
  }
  return originalLabel;
};

const getDynamicUniversitiesLabel = (originalLabel: string, dataCount: number, t: any) => {
  const match = originalLabel.match(/Top Universities? in (.+)/);
  if (match && match[1]) {
    const countryName = match[1];
    const universityWord =
      dataCount === 1 ? t("home.searchbar.topuniversiy") : t("home.searchbar.topuniversities");
    return `${universityWord} ${t("home.searchbar.topuniversitiesin")} ${countryName}`;
  }
  if (originalLabel === "Top Universities" || originalLabel === "Top University") {
    return dataCount === 1 ? t("home.searchbar.topuniversiy") : t("home.searchbar.topuniversities");
  }
  return originalLabel;
};

const DefaultSuggestionsMobile = ({
  gtagEventCategory,
  setShowSearchModal,
  fromSearchPage = false,
}: {
  gtagEventCategory?: any;
  setShowSearchModal?: any;
  fromSearchPage?: boolean;
}) => {
  const {
    isWhitelabel,
    countryWiseCityData,
    countryWiseUniversityData,
    citiesList,
    universitiesList,
    isRestrictedSearchEnabled,
    getFilteredRecentlyVisited,
  } = useWhitelabel();
  const { t } = useTranslationHook();
  const history = useHistory();
  const showPropertiesNearMe = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.PROPERTIES_NEAR_ME);
  const isPersonalizedSearchEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.PERSONALIZED_SEARCH_SUGGESTIONS) ||
    config.IS_CHINA_APP;

  const handleNearMeNavigate = (url: string) => {
    customHistoryPush(history, url);
  };

  const countriesForWhichCitiesArePresent = Object.keys(countryWiseCityData);
  const countriesFroWhichUniversitiesArePresent = Object.keys(countryWiseUniversityData);

  const whitelabelSuggestions = WHITELABEL_DEFAULT_SUGGESTIONS(t);
  const whitelabelSuggestionsData = whitelabelSuggestions.reduce((acc, suggestion) => {
    if (suggestion?.label === "ALL") {
      acc.push({
        ...suggestion,
        top_cities: {
          label: "Top Cities",
          data: citiesList.slice(0, 10),
        },
        top_universities: {
          label: "Top Universities",
          data: universitiesList.slice(0, 10),
        },
      });
    } else if (
      suggestion?.country_full_name &&
      (countriesForWhichCitiesArePresent.includes(suggestion.country_full_name) ||
        countriesFroWhichUniversitiesArePresent.includes(suggestion.country_full_name))
    ) {
      acc.push({
        ...suggestion,
        top_cities: {
          label: `${t("Top Cities in")} ${suggestion?.country_full_name}`,
          data: countryWiseCityData[`${suggestion?.country_full_name}`],
        },
        top_universities: {
          label: `${t("Top Universities in")} ${suggestion?.country_full_name}`,
          data: countryWiseUniversityData[`${suggestion?.country_full_name}`],
        },
      });
    }

    return acc;
  }, [] as any);

  const [suggestionsData, setSuggestionsData]: [any, any] = useState(
    isWhitelabel && isRestrictedSearchEnabled ? whitelabelSuggestionsData : DEFAULT_SUGGESTIONS,
  );
  const [recentlyVisited, setRecentlyVisited] = useState<any[]>([]);
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isDesktop = useIsDesktop();

  const showSuggestedUniversities = (tab: any) => tab.top_universities?.data?.length > 0;

  const getSuggestions = async () => {
    setIsLoading(true);
    const res: any = await getSearchSuggestions({ isPersonalizedSearchEnabled });
    const responseData = res?.data?.data;

    // If personalized response, use it entirely (replace static data)
    if (responseData?.personalized && responseData?.data?.length > 0) {
      const personalizedData = responseData.data;
      setIsPersonalized(true);

      // Country tabs from backend response (already in correct order) — no ALL tab in personalized mode
      const countryTabs = personalizedData.map((country: any) => {
        const isAllTab =
          !country.country_full_name ||
          country.country_full_name === "ALL" ||
          country.label === "ALL";
        return {
          ...country,
          top_cities: {
            ...country.top_cities,
            label: isAllTab
              ? t("home.searchbar.topcity.topcities")
              : `${t("home.searchbar.topcity.topcities")} ${t("home.searchbar.topcitiesin")} ${
                  country.country_full_name
                }`,
          },
          top_universities: {
            ...country.top_universities,
            label: isAllTab
              ? t("home.searchbar.topuniversities")
              : `${t("home.searchbar.topuniversities")} ${t("home.searchbar.topuniversitiesin")} ${
                  country.country_full_name
                }`,
          },
        };
      });

      setSuggestionsData(countryTabs);
      setIsLoading(false);
    } else {
      // Fallback: merge universities into static data (old behavior)
      const tempSuggestionsData = [...suggestionsData];
      tempSuggestionsData?.forEach((_, i, arr) => {
        const countryName = arr[i]?.country_full_name;
        responseData?.data?.forEach((suggestion: any) => {
          if (suggestion?.country_full_name == countryName) {
            arr[i]["top_universities"] = suggestion?.top_universities;
          }
        });
      });
      setSuggestionsData(tempSuggestionsData);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    try {
      const filteredResult = getFilteredRecentlyVisited();
      setRecentlyVisited(filteredResult);
    } catch (error) {
      setRecentlyVisited([]);
    }
    if (!suggestionsData[0]?.top_universities && (!isWhitelabel || !isRestrictedSearchEnabled)) {
      getSuggestions();
    } else {
      setIsLoading(false);
    }
    handleCountryClick("ALL", false);
  }, []);

  // Re-fetch when personalized flag becomes true (GrowthBook resolves after initial mount)
  const hasRefetchedForPersonalized = useRef(false);
  useEffect(() => {
    if (
      isPersonalizedSearchEnabled &&
      !isPersonalized &&
      !hasRefetchedForPersonalized.current &&
      !isWhitelabel
    ) {
      hasRefetchedForPersonalized.current = true;
      getSuggestions();
    }
  }, [isPersonalizedSearchEnabled]);

  // When personalized data loads, activate the first country tab instead of "ALL"
  useEffect(() => {
    if (isPersonalized && suggestionsData.length > 0) {
      const firstTab = suggestionsData[0];
      handleCountryClick(firstTab.label ?? firstTab.country_alias, false);
    }
  }, [isPersonalized, suggestionsData]);

  const handleGtagEvents = ({
    label,
    action,
    canonical_name,
    entity_name,
    entity_type,
    entity_grid,
    suggestion_rank,
    suggestion_count,
  }: {
    label?: any;
    action?: any;
    canonical_name?: any;
    entity_name?: any;
    entity_type?: any;
    entity_grid?: any;
    suggestion_rank?: number | null;
    suggestion_count?: number;
  }) => {
    if (gtagEventCategory)
      gtagEventEmitter({
        action,
        category: gtagEventCategory,
        label,
        canonical_name,
        entity_name,
        entity_type,
        entity_grid,
        suggestion_rank,
        suggestion_count,
        market: config.IS_CHINA_APP ? "china" : "global",
      });
  };

  const handleGtagScrollEvents = ({ action, entity_grid }: { action: any; entity_grid: any }) => {
    if (gtagEventCategory) gtagEventEmitter({ action, category: gtagEventCategory, entity_grid });
  };

  const tabRef = useRef<HTMLDivElement>(null);

  const handleCountryClick = (label: any, fireGAEvent = true) => {
    const container = tabRef.current;
    if (!container) return;

    container.querySelectorAll('[data-val="tab-content"]').forEach((element: any) => {
      if (element.getAttribute("data-value") == label) {
        element.style.display = "block";
        if (isDesktop) {
          element.classList.add(styles.panelDesktopScroll);
        } else {
          element.classList.remove(styles.panelDesktopScroll);
        }
      } else {
        element.style.display = "none";
        element.classList?.remove?.(styles.panelDesktopScroll);
      }
    });
    container.querySelectorAll("[data-active]").forEach((element) => {
      if (element.getAttribute("data-value") == label) {
        element.setAttribute("data-active", "true");
      } else {
        element.setAttribute("data-active", "false");
      }
    });
    if (fireGAEvent && gtagEventCategory)
      gtagEventEmitter({
        action: "clicked_country_select",
        category: gtagEventCategory,
        label: `Search Bar, ${label}`,
      });
  };
  return (
    <div className={styles.tabsContainer} ref={tabRef} data-value="ALL" color="primary.4">
      {isLoading ? (
        <div className={styles.skeletonContainer}>
          <div className={styles.skeletonTabList}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={styles.skeletonTab} />
            ))}
          </div>
          <div className={styles.skeletonContent}>
            <div className={styles.skeletonSectionTitle} />
            <div className={styles.skeletonCityList}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={styles.skeletonCity} />
              ))}
            </div>
            <div className={styles.skeletonSectionTitle} />
            <div className={styles.skeletonCityList}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={styles.skeletonCity} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.tabList}>
            {suggestionsData.map((tab: any, index: number) => (
              <div
                data-testid={tab.label ?? tab.country_alias}
                key={tab.id ?? ""}
                role="none"
                data-active={index === 0 ? "true" : "false"}
                data-value={tab.label ?? tab.country_alias}
                className={styles.tab}
                onClick={() => {
                  handleCountryClick(tab.label ?? tab.country_alias);
                }}
              >
                <div className={styles.tabBox}>
                  {tab.label != "ALL" && tab.country_flag_image && (
                    <Image
                      height={22}
                      width={22}
                      className={styles.flagImage}
                      src={FLAG_BASE_URL + tab.country_flag_image}
                      alt={tab.country_full_name}
                    />
                  )}
                  {tab.label ?? tab.country_alias}
                </div>
              </div>
            ))}
          </div>
          {showPropertiesNearMe && !isDesktop && <NearMeOption onNavigate={handleNearMeNavigate} />}

          {suggestionsData.map((tab: any, index: number) => (
            <div
              data-value={tab.label ?? tab.country_alias}
              data-val="tab-content"
              key={tab.id ?? ""}
              className={styles.panel}
              style={{ display: index === 0 ? "block" : "none" }}
            >
              {showPropertiesNearMe && isDesktop && (
                <NearMeOption onNavigate={handleNearMeNavigate} />
              )}
              {recentlyVisited?.length > 0 && (isPersonalized || tab.label) && (
                <>
                  <CitySuggestionsMobile
                    setShowSearchModal={setShowSearchModal}
                    cities={recentlyVisited}
                    title={t("Recently Searched")}
                    key={`${tab.id ?? ""}cs1`}
                    handleGtagEvents={handleGtagEvents}
                    handleGtagScrollEvents={handleGtagScrollEvents}
                    gtagEventAction="clicked_recently_searched_result"
                    gtagScrollAction="scrolled_recently_searched_result"
                    dataTestId="mobile-search-modal-recentlySearched"
                    fromSearchPage={fromSearchPage}
                    singleRowScrollable={isPersonalized}
                  />
                  {!isDesktop && <div className={styles.divider} />}
                </>
              )}
              {tab.top_cities && tab.top_cities.data?.length > 0 && (
                <CitySuggestionsMobile
                  setShowSearchModal={setShowSearchModal}
                  key={`${tab.id ?? ""}cs2`}
                  cities={tab.top_cities.data}
                  title={`${getLocalLanguageRegionName(
                    getDynamicCitiesLabel(
                      tab.top_cities.label,
                      tab.top_cities.data?.length || 0,
                      t,
                    ),
                    t,
                  )} ⚡`}
                  handleGtagEvents={handleGtagEvents}
                  handleGtagScrollEvents={handleGtagScrollEvents}
                  gtagScrollAction="scrolled_popular_locations_result"
                  gtagEventAction="clicked_popular_locations_result"
                  fromSearchPage={fromSearchPage}
                  singleRowScrollable={false}
                  twoRowScrollable={isPersonalizedSearchEnabled}
                  backendSorted={tab.country_full_name === "ALL"}
                />
              )}
              {!isDesktop && <div className={styles.divider} />}
              {tab.top_universities && showSuggestedUniversities(tab) && (
                <CitySuggestionsMobile
                  setShowSearchModal={setShowSearchModal}
                  key={`${tab.id ?? ""}cs3`}
                  cities={tab.top_universities.data}
                  title={`${getLocalLanguageRegionName(
                    getDynamicUniversitiesLabel(
                      tab.top_universities.label,
                      tab.top_universities.data?.length || 0,
                      t,
                    ),
                    t,
                  )} ⚡`}
                  handleGtagEvents={handleGtagEvents}
                  handleGtagScrollEvents={handleGtagScrollEvents}
                  gtagEventAction="clicked_top_universities_result"
                  gtagScrollAction="scrolled_top_universities_result"
                  fromSearchPage={fromSearchPage}
                  singleRowScrollable={false}
                  twoRowScrollable={isPersonalizedSearchEnabled}
                  backendSorted={tab.country_full_name === "ALL"}
                />
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default wrapperHOC(DefaultSuggestionsMobile, {
  componentName: "DefaultSuggestionsMobile-SearchModalMobile-Global",
  showForChina: true,
});
